'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { dealQueryKeys } from '@lib/deals/hooks/deal-query-keys';
import { useCreateDeal } from '@lib/deals/hooks/use-create-deal';
import { pdfBaseName } from '@lib/everything-bar/utils/pdf-utils';
import { useFlowDocumentProcessingRetry } from '@lib/flows/hooks/use-flow-document-processing-retry';
import { useIsEarlyBuilderOpenEnabled } from '@lib/flows/hooks/use-is-early-builder-open-enabled';
import { useUploadFlowDocumentV2 } from '@lib/flows/hooks/use-upload-flow-document-v2';
import { FLOW_DOCUMENT_PROCESSING_RETRY_GENERIC_ERROR_MESSAGE } from '@lib/flows/utils/apply-flow-document-processing-retry-error';
import { resolveUserFacingCollectionMetaError } from '@lib/flows/utils/format-user-facing-document-processing-error';
import { createFlowDocumentProcessingRetryProgressListener, invokeFlowDocumentProcessingRetryOrFallback, resolveFlowDocumentProcessingRetryTarget, resolveFlowDocumentProcessingRetryTargetFromUploadBatch } from '@lib/flows/utils/resolve-flow-document-processing-retry-target';
import { resolveBatchUploadErrorMessage } from '@lib/flows/utils/resolve-upload-error-message';
import { useImportQueueRail } from '@lib/import-queue-ui/context/import-queue-rail-context';
import { scheduleImportQueueRowRetry } from '@lib/import-queue-ui/utils/schedule-import-queue-row-retry';
import { packageQueryKeys } from '@lib/packages/hooks/package-query-keys';
import { useQueryClient } from '@lib/query/client';
import { flushSync } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { TEMPLATE_BULK_IMPORT_COLLAPSED_KEY } from '../constants/template-bulk-import-storage-key';
import type { TemplateBulkImportQueueItem } from '../types/template-bulk-import-queue-item';
import { buildTemplateDocsPath } from '../utils/build-template-docs-path';
import { fetchFirstPackageIdForDeal } from '../utils/fetch-first-package-id-client';
import { TemplateBulkImportContext, type TemplateBulkImportContextValue } from './template-bulk-import-context';
interface TemplateBulkImportProviderProps {
  children: ReactNode;
}
export function TemplateBulkImportProvider({
  children
}: TemplateBulkImportProviderProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const createDeal = useCreateDeal();
  const {
    uploadBatch
  } = useUploadFlowDocumentV2();
  const {
    retryDocumentProcessing
  } = useFlowDocumentProcessingRetry();
  const earlyBuilderOpenEnabled = useIsEarlyBuilderOpenEnabled();
  // Requires <ImportQueueRailProvider> above in the tree; the provider auto-opens
  // the unified rail when a new import starts.
  const {
    setOpen: setImportQueueRailOpen
  } = useImportQueueRail();
  const [queue, setQueue] = useState<TemplateBulkImportQueueItem[]>([]);
  const [sectionCollapsed, setSectionCollapsedState] = useState(false);
  const hasNavigatedForBatchRef = useRef(false);
  useEffect(() => {
    const stored = window.localStorage.getItem(TEMPLATE_BULK_IMPORT_COLLAPSED_KEY);
    if (stored === 'true') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- restore collapsed preference after mount (localStorage not available during SSR)
      setSectionCollapsedState(true);
    }
  }, []);
  const setSectionCollapsed = useCallback((collapsed: boolean) => {
    setSectionCollapsedState(collapsed);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TEMPLATE_BULK_IMPORT_COLLAPSED_KEY, String(collapsed));
    }
  }, []);
  const patchItem = useCallback((clientId: string, updates: Partial<Pick<TemplateBulkImportQueueItem, 'status' | 'dealId' | 'packageId' | 'flowId' | 'collectionId' | 'errorMessage'>>) => {
    setQueue(prev => prev.map(row => row.clientId === clientId ? {
      ...row,
      ...updates
    } : row));
  }, []);
  const executeImportRow = useCallback(async (row_0: TemplateBulkImportQueueItem) => {
    const {
      clientId: clientId_0,
      file
    } = row_0;
    let dealId = row_0.dealId;
    let packageId = row_0.packageId;
    let processingRetryTarget: {
      flowId: string;
      collectionId: string;
    } | null = null;
    try {
      if (!dealId) {
        patchItem(clientId_0, {
          status: 'creating_deal'
        });
        const deal = await createDeal.mutateAsync({
          name: pdfBaseName(file),
          isTemplate: true,
          parentSpaceNodeId: row_0.parentSpaceNodeId,
          teamId: row_0.teamId
        });
        dealId = deal.id;
        patchItem(clientId_0, {
          dealId
        });
      }
      if (!packageId) {
        packageId = await fetchFirstPackageIdForDeal(dealId);
        patchItem(clientId_0, {
          packageId
        });
      }
      patchItem(clientId_0, {
        status: 'uploading'
      });
      const captureProcessingRetryTarget = createFlowDocumentProcessingRetryProgressListener(target => {
        processingRetryTarget = target;
        patchItem(clientId_0, {
          flowId: target.flowId,
          collectionId: target.collectionId
        });
      });
      const uploadResult = await uploadBatch({
        files: [file],
        dealId,
        packageId,
        uploadSource: 'template_bulk_import',
        isTemplate: true,
        earlyNavigateEnabled: earlyBuilderOpenEnabled,
        onProcessingProgress: captureProcessingRetryTarget,
        onFirstWaveReady: captureProcessingRetryTarget,
        onEarlyNavigate: href => {
          if (hasNavigatedForBatchRef.current) return;
          hasNavigatedForBatchRef.current = true;
          router.replace(href);
        }
      });
      if (uploadResult.summary.failed > 0) {
        processingRetryTarget = resolveFlowDocumentProcessingRetryTargetFromUploadBatch({
          items: uploadResult.items,
          fromProgress: processingRetryTarget
        });
        throw new Error(resolveBatchUploadErrorMessage(uploadResult));
      }
      patchItem(clientId_0, {
        status: 'done',
        errorMessage: undefined
      });
      await queryClient.invalidateQueries({
        queryKey: dealQueryKeys.all
      });
      await queryClient.invalidateQueries({
        queryKey: packageQueryKeys.lists()
      });
      if (!hasNavigatedForBatchRef.current) {
        hasNavigatedForBatchRef.current = true;
        router.replace(buildTemplateDocsPath(dealId, packageId));
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const resolvedTarget = resolveFlowDocumentProcessingRetryTarget({
        error: e,
        fromProgress: processingRetryTarget
      });
      patchItem(clientId_0, {
        status: 'failed',
        errorMessage: message,
        ...(resolvedTarget ? {
          flowId: resolvedTarget.flowId,
          collectionId: resolvedTarget.collectionId
        } : {
          flowId: undefined,
          collectionId: undefined
        })
      });
    }
  }, [createDeal, uploadBatch, earlyBuilderOpenEnabled, queryClient, router, patchItem]);
  const startImportFromFiles = useCallback(async (filesWithPaths: Array<{
    file: File;
    relativePath: string;
    parentSpaceNodeId: string;
  }>, _rootNodeId: string, teamId?: string) => {
    if (filesWithPaths.length === 0) return;
    setSectionCollapsedState(false);
    setImportQueueRailOpen(true);
    hasNavigatedForBatchRef.current = false;
    const newItems: TemplateBulkImportQueueItem[] = filesWithPaths.map(({
      file: file_0,
      relativePath,
      parentSpaceNodeId
    }) => ({
      clientId: uuidv4(),
      file: file_0,
      status: 'queued',
      relativePath,
      parentSpaceNodeId,
      teamId
    }));
    flushSync(() => {
      setQueue(prev_0 => [...prev_0, ...newItems]);
    });
    for (let i = 0; i < newItems.length; i++) {
      await executeImportRow(newItems[i]);
      if (i < newItems.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  }, [executeImportRow, setImportQueueRailOpen]);
  const retryItem = useCallback((clientId_1: string) => {
    const row_1 = queue.find(r => r.clientId === clientId_1);
    if (!row_1) return;
    const processingTarget = resolveFlowDocumentProcessingRetryTarget({
      fromProgress: row_1.flowId && row_1.collectionId ? {
        flowId: row_1.flowId,
        collectionId: row_1.collectionId
      } : null
    });
    void (async () => {
      const invocation = await invokeFlowDocumentProcessingRetryOrFallback({
        target: processingTarget,
        retryDocumentProcessing,
        onBeforeProcessingRetry: () => {
          patchItem(clientId_1, {
            status: 'uploading',
            errorMessage: undefined
          });
        },
        onReupload: () => {
          scheduleImportQueueRowRetry({
            clientId: clientId_1,
            queue,
            setQueue,
            toQueuedRow: queuedRow => ({
              ...queuedRow,
              status: 'queued' as const,
              errorMessage: undefined
            }),
            execute: payload => executeImportRow(payload)
          });
        }
      });
      if (invocation.path === 'reupload') {
        return;
      }
      const {
        result
      } = invocation;
      if (result?.status === 'requeued') {
        patchItem(clientId_1, {
          status: 'done',
          errorMessage: undefined
        });
        await queryClient.invalidateQueries({
          queryKey: dealQueryKeys.all
        });
        await queryClient.invalidateQueries({
          queryKey: packageQueryKeys.lists()
        });
        return;
      }
      if (result?.status === 'needs_reupload') {
        patchItem(clientId_1, {
          status: 'failed',
          flowId: undefined,
          collectionId: undefined,
          errorMessage: resolveUserFacingCollectionMetaError({
            needsReupload: true
          }) ?? 'Uploaded file could not be read. Please try again.'
        });
        return;
      }
      if (result?.status === 'failed') {
        patchItem(clientId_1, {
          status: 'failed',
          errorMessage: resolveUserFacingCollectionMetaError({
            status: 'failed'
          }) ?? 'Document processing failed. Please try again.'
        });
        return;
      }
      patchItem(clientId_1, {
        status: 'failed',
        errorMessage: FLOW_DOCUMENT_PROCESSING_RETRY_GENERIC_ERROR_MESSAGE
      });
    })();
  }, [queue, executeImportRow, patchItem, queryClient, retryDocumentProcessing]);
  const hasActiveWork = useMemo(() => queue.some(row_2 => ['queued', 'creating_deal', 'uploading'].includes(row_2.status)), [queue]);
  const value = useMemo<TemplateBulkImportContextValue>(() => ({
    queue,
    sectionCollapsed,
    setSectionCollapsed,
    startImportFromFiles,
    retryItem,
    hasActiveWork
  }), [queue, sectionCollapsed, setSectionCollapsed, startImportFromFiles, retryItem, hasActiveWork]);
  return <TemplateBulkImportContext.Provider value={value} data-sentry-element="TemplateBulkImportContext.Provider" data-sentry-component="TemplateBulkImportProvider" data-sentry-source-file="template-bulk-import-provider.tsx">
      {children}
    </TemplateBulkImportContext.Provider>;
}